// Supplier create/edit form, used by the Suppliers tab and from the purchase
// invoice editor so a new supplier can be added without losing the entry.

import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { fieldErrors, useErrorMessage } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { Checkbox, ErrorAlert, TextAreaField, TextField } from './ui';
import type { Supplier } from '@shared/types';

export function SupplierForm({ supplier, onSaved, onCancel }: {
  supplier?: Supplier;
  onSaved: (supplier: Supplier, created: boolean) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const toMessage = useErrorMessage();

  const [name, setName] = useState(supplier?.name ?? '');
  const [taxId, setTaxId] = useState(supplier?.taxId ?? '');
  const [phone, setPhone] = useState(supplier?.phone ?? '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [address, setAddress] = useState(supplier?.address ?? '');
  const [notes, setNotes] = useState(supplier?.notes ?? '');
  const [isActive, setIsActive] = useState(supplier?.isActive ?? true);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    setFormError(undefined);
    const payload = { name: name.trim(), taxId, phone, email, address, notes, isActive };
    try {
      const saved = supplier
        ? await api.updateSupplier(supplier.id, payload)
        : await api.createSupplier(payload);
      onSaved(saved, !supplier);
    } catch (err) {
      const fields = fieldErrors(err);
      setErrors(fields);
      if (Object.keys(fields).length === 0) setFormError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="stack">
      {formError && <ErrorAlert message={formError} />}
      <div className="form-grid form-grid--2">
        <TextField label={t('suppliers.field.name')} required autoFocus wrapperClassName="span-all"
          value={name} error={errors.name} onChange={(e) => setName(e.target.value)} />
        <TextField label={t('suppliers.field.taxId')} optional
          value={taxId} error={errors.taxId} onChange={(e) => setTaxId(e.target.value)} />
        <TextField label={t('suppliers.field.phone')} optional type="tel"
          value={phone} error={errors.phone} onChange={(e) => setPhone(e.target.value)} />
        <TextField label={t('suppliers.field.email')} optional type="email" wrapperClassName="span-all"
          value={email} error={errors.email} onChange={(e) => setEmail(e.target.value)} />
        <TextAreaField label={t('suppliers.field.address')} optional rows={2} wrapperClassName="span-all"
          value={address} error={errors.address} onChange={(e) => setAddress(e.target.value)} />
        <TextAreaField label={t('suppliers.field.notes')} optional rows={2} wrapperClassName="span-all"
          value={notes} error={errors.notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <Checkbox label={t('common.active')} checked={isActive} onChange={setIsActive} />
      <div className="row row--end">
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy && <span className="spinner" />}
          {busy ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </form>
  );
}
